import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Alert, Anchor, TextButton } from '@zillow/constellation-10';

import { useRentalManagerUserPropertyContactEmailWebApiHandler_purge } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { getClient } from '@zg-rentals/ts-rental-manager-api-beta-client';

import { useSelector } from '../../selectors/useSelector';

import PropertiesActions from '../../actions/PropertiesActions';

import ContactEmailDeliverability from '../../model/ContactEmailDeliverability';

const LEARN_MORE_LINK = 'https://help.zillowrentalmanager.com/hc/en-us/articles/360052675773';

const ContactEmailDeliverabilityAlert = () => {
  const dispatch = useDispatch();

  const {
    contactEmailDeliverability: { contactEmail, isDeliverable, isPurgeLimitExceeded, supportEmail },
    listingAlias,
  } = useSelector((state) => state.properties.currentProperty);

  const [shouldRenderBanner, setShouldRenderBanner] = useState(false);

  const {
    mutate: purgeContactEmail,
    isError: hasEmailPurgeError,
    data: emailPurgeData,
  } = useRentalManagerUserPropertyContactEmailWebApiHandler_purge({
    httpClient: getClient()?.extend({ timeout: 5000 }),
  });

  useEffect(() => {
    if (isDeliverable !== undefined) {
      setShouldRenderBanner(!isDeliverable);
    }
  }, [isDeliverable]);

  useEffect(() => {
    if (emailPurgeData) {
      dispatch(
        PropertiesActions.updateCurrentProperty({
          contactEmailDeliverability: ContactEmailDeliverability(emailPurgeData),
        }),
      );
    }
  }, [emailPurgeData]);

  const handleConfirmClick = () => {
    purgeContactEmail({ getParams: { listingAlias } });
  };

  const renderDefaultContent = () => {
    return (
      <span data-testid="DeliveryIssueContent">
        We&apos;ve detected a deliverability issue with your contact email: {contactEmail}.
      </span>
    );
  };

  const renderPurgeLimitExceededContent = () => {
    return (
      <div data-testid="PurgeLimitExceededContent">
        <span>We&apos;ve detected a deliverability issue with your contact email: {contactEmail}. Please contact </span>
        <Anchor href={`mailto:${supportEmail}`} target="_blank" rel="noreferrer">
          customer support
        </Anchor>
        <span>.</span>
      </div>
    );
  };

  const renderBody = () => {
    return isPurgeLimitExceeded ? renderPurgeLimitExceededContent() : renderDefaultContent();
  };

  const renderEmailPurgeError = () => {
    return (
      <div>
        <span>Action failed.&nbsp;</span>
        <TextButton onClick={handleConfirmClick}>Try again</TextButton>
      </div>
    );
  };

  const renderDefaultActionButton = () => {
    return (
      <div>
        <TextButton onClick={handleConfirmClick}>Confirm</TextButton>
        <span>
          &nbsp;the email is correct or&nbsp;
          <Anchor href={LEARN_MORE_LINK} target="_blank" rel="noreferrer">
            learn more
          </Anchor>
          .
        </span>
      </div>
    );
  };

  const renderActionButton = () => {
    return hasEmailPurgeError ? renderEmailPurgeError() : renderDefaultActionButton();
  };

  if (!shouldRenderBanner) {
    return null;
  }

  return (
    <Alert
      data-testid="ContactEmailDeliverabilityAlert"
      tone="critical"
      actionButton={!isPurgeLimitExceeded && renderActionButton()}
    >
      {renderBody()}
    </Alert>
  );
};

export default ContactEmailDeliverabilityAlert;
